import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';

import type { IAuthorization } from '@contexts/auth/IUseAuth';
import { StorageKeyAuthorization } from '@contexts/auth/provider';

import { callRedirect } from '../Redirect';

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

let isRefreshing = false;

let failedQueue: Array<{
  reject: (error: unknown) => void;
  resolve: (token: string) => void;
}> = [];

function processQueue(error: unknown, token?: string) {
  failedQueue.forEach(promise => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token!);
    }
  });
  failedQueue = [];
}

const refreshHttpClient = axios.create({
  baseURL: import.meta.env.VITE_BACKEND_URL,
  timeout: 5000,
});

export function refresh(httpClient: AxiosInstance): void {
  httpClient.interceptors.response.use(
    response => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as
        | CustomAxiosRequestConfig
        | undefined;

      if (
        !originalRequest ||
        error.response?.status !== 401 ||
        originalRequest._retry
      ) {
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({
            resolve: token => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              resolve(httpClient(originalRequest));
            },
            reject,
          });
        });
      }

      isRefreshing = true;

      try {
        const storageToken = localStorage.getItem(StorageKeyAuthorization);
        const storedTokenParsed: IAuthorization = JSON.parse(storageToken!);
        const refreshResponse = await refreshHttpClient.patch(
          'collaborators/sessions/refresh',
          {
            refresh_token: storedTokenParsed.refreshToken as string,
          },
        );
        const newAuthorization = refreshResponse.data;
        localStorage.setItem(
          StorageKeyAuthorization,
          JSON.stringify({
            token: newAuthorization.token,
            refreshToken: newAuthorization.refresh_token,
          }),
        );
        processQueue(null, newAuthorization.token);
        originalRequest.headers.Authorization = `Bearer ${newAuthorization.token}`;
        return httpClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        callRedirect();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    },
  );
}
